import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useBuilderStore } from '../../stores/builderStore';
import FormRenderer from '../renderer/FormRenderer';

type DeviceType = 'desktop' | 'tablet' | 'mobile';

interface DeviceConfig {
  name: string;
  width: number;
  height: number;
  icon: string;
}

const DEVICES: Record<DeviceType, DeviceConfig> = {
  desktop: { name: 'Desktop', width: 1200, height: 800, icon: '🖥️' },
  tablet: { name: 'Tablet', width: 768, height: 1024, icon: '📱' },
  mobile: { name: 'Mobile', width: 375, height: 667, icon: '📱' },
};

interface FormPreviewProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FormPreview({ isOpen, onClose }: FormPreviewProps) {
  const { formSchema, formId } = useBuilderStore();
  const [device, setDevice] = useState<DeviceType>('desktop');
  const [scale, setScale] = useState(1);

  // Calculate scale based on available space
  useEffect(() => {
    if (!isOpen) return;

    const calculateScale = () => {
      const deviceConfig = DEVICES[device];
      const availableWidth = window.innerWidth - 100; // padding
      const availableHeight = window.innerHeight - 200; // header + padding

      const scaleX = availableWidth / deviceConfig.width;
      const scaleY = availableHeight / deviceConfig.height;
      const newScale = Math.min(scaleX, scaleY, 1);

      setScale(newScale);
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, [isOpen, device]);

  // Close on Escape key and manage body overflow
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      // Save the original overflow value
      const originalOverflow = document.body.style.overflow;
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        // Restore the original overflow value
        document.body.style.overflow = originalOverflow;
      };
    }

    return undefined;
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSuccess = () => {
    // In preview mode, just show a success message
    alert('Form submitted successfully! (Preview mode - no data saved)');
  };

  const handleError = (error: Error) => {
    alert(`Submission error: ${error.message}`);
  };

  const deviceConfig = DEVICES[device];

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative bg-gray-100 rounded-xl shadow-2xl max-w-[95vw] max-h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900">Form Preview</h2>

            {/* Device Selector */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {(Object.keys(DEVICES) as DeviceType[]).map((deviceType) => (
                <button
                  key={deviceType}
                  onClick={() => setDevice(deviceType)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    device === deviceType
                      ? 'bg-white text-primary-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title={DEVICES[deviceType].name}
                >
                  <span className="mr-1.5">{DEVICES[deviceType].icon}</span>
                  {DEVICES[deviceType].name}
                </button>
              ))}
            </div>

            {/* Device Dimensions */}
            <span className="text-sm text-gray-500">
              {deviceConfig.width} x {deviceConfig.height}
            </span>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            title="Close (Esc)"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Preview Area */}
        <div className="flex-1 p-8 flex items-center justify-center overflow-auto bg-[#1a1a2e]">
          {/* Device Frame */}
          <div
            className="relative bg-gray-800 rounded-3xl p-3 shadow-2xl"
            style={{
              transform: `scale(${scale})`,
              transformOrigin: 'center center',
            }}
          >
            {/* Device Notch (for mobile) */}
            {device === 'mobile' && (
              <div className="absolute top-6 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-800 rounded-b-xl z-10" />
            )}

            {/* Screen */}
            <div
              className="bg-white rounded-2xl overflow-hidden shadow-inner"
              style={{
                width: deviceConfig.width,
                height: deviceConfig.height,
              }}
            >
              <div className="h-full overflow-auto">
                {formSchema.fields.length > 0 ? (
                  <FormRenderer
                    formId={formId || 'preview'}
                    schema={formSchema}
                    onSuccess={handleSuccess}
                    onError={handleError}
                  />
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-lg font-medium">No fields yet</p>
                      <p className="text-sm">Add fields to preview your form</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Home Indicator (for mobile/tablet) */}
            {(device === 'mobile' || device === 'tablet') && (
              <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-32 h-1 bg-gray-600 rounded-full" />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Preview mode - form submissions will not be saved
          </p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">
              Scale: {Math.round(scale * 100)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
